const encodePathSegments = (pathname: string) =>
  pathname
    .split('/')
    .map((segment) => {
      if (!segment) return segment

      try {
        return encodeURIComponent(decodeURIComponent(segment))
      } catch {
        return encodeURIComponent(segment)
      }
    })
    .join('/')

export const normalizeFileURL = (url: string): string => {
  if (!url) return url

  try {
    const parsedUrl = new URL(url)
    parsedUrl.pathname = encodePathSegments(parsedUrl.pathname)
    return parsedUrl.toString()
  } catch {
    return url
  }
}

export const getDownloadFileName = (url: string, fallbackName = 'download') => {
  const urlParts = url.split('/')
  const fileName = urlParts[urlParts.length - 1] || fallbackName

  return fileName.split('?')[0] || fallbackName
}

interface IDownloadFileFromURLProps {
  fallbackFileName?: string
  fallbackToDirectLink?: boolean
  fetchOptions?: RequestInit
  openInNewTab?: boolean
  url: string
}

const triggerAnchorDownload = ({
  href,
  fileName,
  openInNewTab
}: {
  href: string
  fileName?: string
  openInNewTab?: boolean
}) => {
  const link = document.createElement('a')
  link.href = href
  if (fileName) link.download = fileName
  if (openInNewTab) {
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
  }
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const downloadFileFromURL = async ({
  fallbackFileName,
  fallbackToDirectLink = true,
  fetchOptions,
  openInNewTab = false,
  url
}: IDownloadFileFromURLProps): Promise<boolean> => {
  if (!url) return false

  const normalizedURL = normalizeFileURL(url)
  const cleanFileName = fallbackFileName || getDownloadFileName(url)

  try {
    const response = await fetch(normalizedURL, fetchOptions ?? { mode: 'cors' })
    if (!response.ok) throw new Error('Failed to fetch file')

    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    triggerAnchorDownload({
      fileName: cleanFileName,
      href: objectUrl,
      openInNewTab
    })
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl)
    }, 0)
    return true
  } catch (error) {
    console.error('Download failed:', error)

    if (!fallbackToDirectLink) return false

    triggerAnchorDownload({
      fileName: cleanFileName,
      href: normalizedURL,
      openInNewTab
    })
    return true
  }
}
